export const initialState = {
    isDarkMode: false,
    mainLayout: 'app',
    theme: 'light',
    menu: 'vertical',
    layout: 'full',
    rtlClass: 'ltr',
    animation: '',
    navbar: 'navbar-sticky',
    locale: 'en',
    sidebar: false,
    languageList: [
        { code: 'zh', name: 'Chinese' },
        { code: 'da', name: 'Danish' },
        { code: 'bn', name: 'Bengali' },
        { code: 'en', name: 'English' },
        { code: 'hi', name: 'Hindi' },
        { code: 'fr', name: 'French' },
        { code: 'de', name: 'German' },
        { code: 'el', name: 'Greek' },
        { code: 'hu', name: 'Hungarian' },
        { code: 'it', name: 'Italian' },
        { code: 'ja', name: 'Japanese' },
        { code: 'mr', name: 'Marathi' },
        { code: 'pl', name: 'Polish' },
        { code: 'pt', name: 'Portuguese' },
        { code: 'ru', name: 'Russian' },
        { code: 'es', name: 'Spanish' },
        { code: 'sv', name: 'Swedish' },
        { code: 'ta', name: 'Tamil' },
        { code: 'te', name: 'Telugu' },
        { code: 'tr', name: 'Turkish' },
        { code: 'ae', name: 'Arabic' },
    ],
    isShowMainLoader: true,
    semidark: false,
};

type IndexState = typeof initialState & { direction?: string };

function resolveIsDarkMode(theme: string, currentIsDarkMode: boolean): boolean {
    if (theme === 'light') {
        return false;
    }
    if (theme === 'dark') {
        return true;
    }
    if (theme === 'system') {
        return globalThis.matchMedia?.('(prefers-color-scheme: dark)')?.matches ?? false;
    }
    return currentIsDarkMode;
}

function applyDarkModeClass(isDarkMode: boolean): void {
    const body = document.querySelector('body');
    if (isDarkMode) {
        body?.classList.add('dark');
    } else {
        body?.classList.remove('dark');
    }
}

function handleToggleDirection(state: IndexState, payload: string): IndexState {
    localStorage.setItem('direction', payload);
    document.querySelector('html')?.setAttribute('dir', payload || 'ltr');
    return { ...state, direction: payload };
}

function handleToggleMainLoader(state: IndexState, payload: boolean): IndexState {
    return { ...state, isShowMainLoader: payload };
}

function handleSetMainLayout(state: IndexState, payload: string): IndexState {
    return { ...state, mainLayout: payload };
}

function handleToggleTheme(state: IndexState, payload: string): IndexState {
    const theme = payload || state.theme;
    localStorage.setItem('theme', theme);
    const isDarkMode = resolveIsDarkMode(theme, state.isDarkMode || false);
    applyDarkModeClass(isDarkMode);
    return { ...state, theme, isDarkMode };
}

function handleToggleMenu(state: IndexState, payload: string): IndexState {
    const menu = payload || state.menu;
    localStorage.setItem('menu', menu);
    return { ...state, sidebar: false, menu };
}

function handleToggleLayout(state: IndexState, payload: string): IndexState {
    const layout = payload || state.layout;
    localStorage.setItem('layout', layout);
    return { ...state, layout };
}

function handleToggleRTL(state: IndexState, payload: string): IndexState {
    const rtlClass = payload || state.rtlClass;
    localStorage.setItem('rtlClass', rtlClass);
    document.querySelector('html')?.setAttribute('dir', rtlClass || 'ltr');
    return { ...state, rtlClass };
}

function handleToggleAnimation(state: IndexState, payload: string): IndexState {
    const animation = payload?.trim();
    localStorage.setItem('animation', animation);
    if (animation) {
        const eleanimation = document.querySelector('.animation');
        eleanimation?.classList.add('animate__animated', animation);
    }
    return { ...state, animation };
}

function handleToggleNavbar(state: IndexState, payload: string): IndexState {
    const navbar = payload || state.navbar;
    localStorage.setItem('navbar', navbar);
    return { ...state, navbar };
}

function handleToggleSemidark(state: IndexState, payload = false): IndexState {
    const semidark = payload;
    localStorage.setItem('semidark', String(semidark));
    return { ...state, semidark };
}

function handleToggleLocale(state: IndexState, payload: string): IndexState {
    const locale = payload || state.locale;
    localStorage.setItem('i18n_locale', locale);
    return { ...state, locale };
}

function handleToggleSidebar(state: IndexState): IndexState {
    return { ...state, sidebar: !state.sidebar };
}

const actionHandlers: Record<string, (state: IndexState, payload: any) => IndexState> = {
    toggleDirection: handleToggleDirection,
    toggleMainLoader: handleToggleMainLoader,
    setMainLayout: handleSetMainLayout,
    toggleTheme: handleToggleTheme,
    toggleMenu: handleToggleMenu,
    toggleLayout: handleToggleLayout,
    toggleRTL: handleToggleRTL,
    toggleAnimation: handleToggleAnimation,
    toggleNavbar: handleToggleNavbar,
    toggleSemidark: handleToggleSemidark,
    toggleLocale: handleToggleLocale,
    toggleSidebar: (state) => handleToggleSidebar(state),
};

export function indexReducer(state = initialState, action: any) {
    const handler = actionHandlers[action.type];
    if (handler) {
        return handler(state, action.payload);
    }
    return state;
}
